import {
  Button,
  Card,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Spin,
  Tag,
  Upload,
  message as antdMessage,
} from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import {
  createEmailTemplate,
  deleteEmailTemplate,
  getEmailTemplate,
  listEmailTemplates,
} from "../api/emailTemplates";
import type { EmailTemplateSummary } from "../api/emailTemplates";
import { formatDateTime } from "../utils/time";

export function EmailTemplatesCard() {
  const [templates, setTemplates] = useState<EmailTemplateSummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<{ name: string; html: string } | null>(null);

  const refresh = () => {
    listEmailTemplates()
      .then((d) => setTemplates(d.templates))
      .catch(() => setTemplates([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  const onPreview = async (id: string, name: string) => {
    try {
      const { template } = await getEmailTemplate(id);
      setPreviewHtml({ name, html: template.html });
    } catch {
      antdMessage.error("加载失败");
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteEmailTemplate(id);
      antdMessage.success("已删除");
      refresh();
    } catch {
      antdMessage.error("删除失败");
    }
  };

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FileTextOutlined /> 邮件模板 (HTML)
        </span>
      }
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setCreating(true)}
        >
          新建
        </Button>
      }
    >
      {templates === null ? (
        <div style={{ display: "grid", placeItems: "center", padding: 16 }}>
          <Spin />
        </div>
      ) : templates.length === 0 ? (
        <Empty description="还没有 HTML 邮件模板" />
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {templates.map((t) => (
            <li
              key={t.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                border: "1px solid var(--ant-color-border)",
                borderRadius: 10,
                background: "var(--ant-color-bg-container)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ant-color-text-secondary)",
                    marginTop: 2,
                  }}
                >
                  {formatDateTime(t.createdAt)}
                </div>
              </div>
              <Tag color="blue">HTML</Tag>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onPreview(t.id, t.name)}
                aria-label="预览"
              />
              <Popconfirm title="删除该模板?" onConfirm={() => onDelete(t.id)}>
                <Button type="text" danger size="small" icon={<DeleteOutlined />} aria-label="删除" />
              </Popconfirm>
            </li>
          ))}
        </ul>
      )}

      <CreateTemplateDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          refresh();
        }}
      />

      <Drawer
        open={!!previewHtml}
        onClose={() => setPreviewHtml(null)}
        title={previewHtml?.name ?? "预览"}
        width={Math.min(880, typeof window !== "undefined" ? window.innerWidth : 880)}
        maskClosable={false}
      >
        {previewHtml ? (
          <iframe
            srcDoc={previewHtml.html}
            title={previewHtml.name}
            sandbox=""
            style={{ width: "100%", height: "calc(100dvh - 120px)", border: "1px solid var(--ant-color-border)", borderRadius: 8 }}
          />
        ) : null}
      </Drawer>
    </Card>
  );
}

function CreateTemplateDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [html, setHtml] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setHtml("");
      setFileName(null);
    }
  }, [open]);

  const onSubmit = async () => {
    if (!name.trim()) {
      antdMessage.warning("请填写模板名");
      return;
    }
    if (!html.trim()) {
      antdMessage.warning("请上传 HTML 文件");
      return;
    }
    setSubmitting(true);
    try {
      await createEmailTemplate({ name: name.trim(), html });
      antdMessage.success("已保存");
      onCreated();
    } catch (err) {
      antdMessage.error(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title="新建 HTML 邮件模板"
      open={open}
      onClose={onClose}
      width={Math.min(640, typeof window !== "undefined" ? window.innerWidth : 640)}
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={submitting} onClick={onSubmit}>
            保存
          </Button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <Label>模板名</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如:产品推广月报 v1"
          />
        </div>
        <div>
          <Label>HTML 文件</Label>
          <Upload.Dragger
            accept=".html,.htm,text/html"
            multiple={false}
            beforeUpload={async (file) => {
              if (file.size > 2 * 1024 * 1024) {
                antdMessage.warning("文件超过 2MB");
                return Upload.LIST_IGNORE;
              }
              const text = await file.text();
              setHtml(text);
              setFileName(file.name);
              return false; // 不真正上传
            }}
            showUploadList={false}
            style={{ padding: "8px" }}
          >
            <div style={{ padding: 16 }}>
              <p style={{ marginBottom: 8 }}>
                <UploadOutlined style={{ fontSize: 22, color: "var(--ant-color-primary)" }} />
              </p>
              <p style={{ marginBottom: 4, fontSize: 15 }}>
                点击或拖拽 .html 文件到此处
              </p>
              <p style={{ fontSize: 13, color: "var(--ant-color-text-secondary)" }}>
                可以放原始 HTML(如 EDM 邮件), &lt; 2MB
              </p>
            </div>
          </Upload.Dragger>
          {fileName ? (
            <div style={{ marginTop: 8, fontSize: 14 }}>
              已选: <Tag color="green">{fileName}</Tag>{" "}
              <span style={{ color: "var(--ant-color-text-secondary)" }}>
                ({(html.length / 1024).toFixed(1)} KB)
              </span>
            </div>
          ) : null}
        </div>
        {html ? (
          <div>
            <Label>预览</Label>
            <iframe
              srcDoc={html}
              title="预览"
              sandbox=""
              style={{
                width: "100%",
                height: 420,
                border: "1px solid var(--ant-color-border)",
                borderRadius: 8,
                background: "white",
              }}
            />
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 14,
        color: "var(--ant-color-text-secondary)",
        marginBottom: 4,
        fontWeight: 500,
      }}
    >
      {children}
    </div>
  );
}
